import { install, gtag } from 'ga-gtag';
import AnalyticsProvider from './AnalyticsProvider';

// Mapping from standardized event data keys to Google Analytics parameter names
const GA_FIELD_MAPPING = {
  department: 'department',
  page_uri: 'page_uri',
  page_type: 'page_type',
  vehicle_group: 'group',
  vehicle_type: 'vehicle_type',
  vehicle_make: 'make',
  vehicle_model: 'model',
  vehicle_submodel: 'submodel',
  vehicle_year: 'year',
  vehicle_condition: 'condition',
  vehicle_location: 'gd_location',
  vehicle_stockid: 'stock_id',
  lead_entry_point: 'lead_entry_point',
  lead_id: 'lead_id',
  promotion_id: 'promotion_id',
  promotion_name: 'promotion_name',
  ad_size: 'ad_size',
  click_category: 'click_category',
  button_title: 'button_title',
};

// Mapping for query string parameters (used for thank you page redirects)
const GA_DIMENSION_MAPPING = {
  department: 'department',
  page_uri: 'pageUri',
  page_type: 'pageType',
  vehicle_condition: 'condition',
  vehicle_location: 'location',
  vehicle_make: 'make',
  vehicle_model: 'model',
  vehicle_stockid: 'stockId',
  vehicle_submodel: 'submodel',
  vehicle_type: 'subcategory',
  vehicle_year: 'year',
  lead_entry_point: 'leadEntryPoint',
  promotion_id: 'promotionId',
  promotion_name: 'promotionName',
  ad_size: 'adSize',
  click_category: 'clickCategory',
  button_title: 'buttonTitle',
};

export default class GoogleAnalyticsProvider extends AnalyticsProvider {
  constructor(config = {}) {
    super(config);
    this.propertyId = config.propertyId || window.Transwest?.ga4PropertyId;
    this.queryString = '';
    this.lastSubmission = null;
  }

  getName() {
    return 'google-analytics';
  }

  isAvailable() {
    return Boolean(this.propertyId);
  }

  async initialize() {
    if (!this.isAvailable()) {
      console.warn(
        'Google Analytics 4 property ID is not set. GA tracking will not be initialized.',
      );
      return false;
    }

    if (this.isInitialized) {
      return true;
    }

    try {
      // Install GA4 script (safe to call multiple times)
      install(this.propertyId, {
        send_page_view: false,
      });

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
      return false;
    }
  }

  trackEvent(eventName, eventData) {
    if (!this.isInitialized) {
      console.warn('Google Analytics provider not initialized');
      return;
    }

    const transformedData = this.transformEventData(eventData);

    console.debug('Google Analytics event:', eventName, transformedData);

    // Store submission for query string generation
    this.lastSubmission = transformedData;
    this.generateQueryString();

    gtag('event', eventName, transformedData);
  }

  setUserProperties(properties) {
    if (!this.isInitialized) {
      console.warn('Google Analytics provider not initialized');
      return;
    }

    gtag('config', this.propertyId, {
      custom_map: properties,
    });
  }

  transformEventData(eventData) {
    const transformed = {};

    Object.entries(GA_FIELD_MAPPING).forEach(([gaKey, formField]) => {
      const value = eventData instanceof FormData
        ? eventData.get(formField)
        : eventData[formField];

      transformed[gaKey] = !value || value === '' ? '(not set)' : value;
    });

    return transformed;
  }

  /**
   * Generate query string for thank you page redirects
   */
  generateQueryString() {
    if (!this.lastSubmission) {
      this.queryString = '';
      return;
    }

    const params = new URLSearchParams();

    Object.entries(this.lastSubmission).forEach(([key, value]) => {
      const mappedKey = GA_DIMENSION_MAPPING[key];

      if (mappedKey && value !== '(not set)') {
        params.set(mappedKey, value);
      }
    });

    this.queryString = params.toString();
  }

  /**
   * Get the query string for thank you page redirects
   * @returns {string}
   */
  getQueryString() {
    console.debug('Google Analytics query string:', this.queryString);
    return this.queryString;
  }
}
